import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import Navigation from './components/Navigation';
import AnalyticsManager from './components/AnalyticsManager';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import AynaraVoiceOrb from './components/AynaraVoiceOrb';
import CookieBanner from './components/CookieBanner';
import ChatBot from './components/ChatBot';
import { Toaster } from './components/ui/sonner';
import { syncTikTokConsent } from './lib/tiktokTracking';
import HomePage from './pages/HomePage';
import SolucionesPage from './pages/SolucionesPage';
import SistemaMultiagenteCorePage from './pages/SistemaMultiagenteCorePage';
import SeguridadAgentesPage from './pages/SeguridadAgentesPage';
import NosotrosPage from './pages/NosotrosPage';
import ContactoPage from './pages/ContactoPage';
import PreciosPage from './pages/PreciosPage';
import SostenibilidadPage from './pages/SostenibilidadPage';
import PagoExitosoPage from './pages/PagoExitosoPage';
import PagoCanceladoPage from './pages/PagoCanceladoPage';
import ContractCompletionPage from './pages/ContractCompletionPage';
import GoogleAccessPage from './pages/GoogleAccessPage';
import PartnersPage from './pages/PartnersPage';
import ProductPage from './pages/ProductPage';
import WhatIsPage from './pages/WhatIsPage';
import ComparisonPage from './pages/ComparisonPage';
import DeveloperPage from './pages/DeveloperPage';
import SecurityPage from './pages/SecurityPage';
import AgencyPage from './pages/AgencyPage';
import AgendaPage from './pages/AgendaPage';
import ActivationFlowPage from './pages/ActivationFlowPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import LoginPage from './pages/LoginPage';
import MediaDemosPage from './pages/MediaDemosPage';
import ClinicalDemoFormPage from './pages/ClinicalDemoFormPage';
import ClinicDemoDashboardPage from './pages/ClinicDemoDashboardPage';
import ClinicWorkspacePage from './pages/ClinicWorkspacePage';
import MlmWorkspacePage from './pages/MlmWorkspacePage';
import AdminRedesPage from './pages/AdminRedesPage';
import TikTokCallbackPage from './pages/TikTokCallbackPage';
import MetaCallbackPage from './pages/MetaCallbackPage';
import ClinicPatientAppPage from './pages/ClinicPatientAppPage';
import Experiencia3DPage from './pages/Experiencia3DPage';
import ContractSignPage from './pages/ContractSignPage';
import PortalClientePage from './pages/PortalClientePage';
import CampanaPage from './pages/CampanaPage';
import AuthCallback from './pages/AuthCallback';
import NeuraliaTesixPage from './pages/NeuraliaTesixPage';
import GeoLimaPage from './pages/GeoLimaPage';
import CRMLandingPage from './pages/CRMLandingPage';
import TesixPeruLandingPage from './pages/TesixPeruLandingPage';
import ConsultoriaEspanaPage from './pages/ConsultoriaEspanaPage';
import AutomatizacionPuertoRicoPage from './pages/AutomatizacionPuertoRicoPage';
import SistemaBrasilPage from './pages/SistemaBrasilPage';
import AgentesPeruPage from './pages/AgentesPeruPage';
import ActivaPeruPage from './pages/ActivaPeruPage';
import AuthLinkAuthorizationPage from './pages/AuthLinkAuthorizationPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import DeepSeekPage from './pages/integraciones/DeepSeekPage';
import AdminClientProvisioningPage from './pages/admin/AdminClientProvisioningPage';
import RecursosPage from './pages/recursos/RecursosPage';
import ArticuloMultiagente from './pages/recursos/ArticuloMultiagente';
import ArticuloDeepSeek from './pages/recursos/ArticuloDeepSeek';
import ArticuloSeguridad from './pages/recursos/ArticuloSeguridad';
import AgentCatalogPage from './pages/AgentCatalogPage';
import UserDashboard from './pages/user/UserDashboard';
import UnirsePage from './pages/UnirsePage';
import DevelopersPage from './pages/DevelopersPage';
import AppMovilPage from './pages/AppMovilPage';
import TendenciasPage from './pages/TendenciasPage';
import NotFoundPage from './pages/NotFoundPage';
import { AvisoLegal, Privacidad, CookiesPage, Terminos } from './pages/LegalPages';
// Academy
import AcademyCatalogPage from './pages/academy/AcademyCatalogPage';
import AcademyCoursePage from './pages/academy/AcademyCoursePage';
import AcademyClassroomPage from './pages/academy/AcademyClassroomPage';
import AcademyCertVerifyPage from './pages/academy/AcademyCertVerifyPage';
import AcademyDashboardPage from './pages/academy/AcademyDashboardPage';
import AcademyLoginPage from './pages/academy/AcademyLoginPage';
import AcademyAdminPage from './pages/academy/AcademyAdminPage';
import AcademyPaymentSuccess from './pages/academy/AcademyPaymentSuccess';
import '@/App.css';

function AppShell() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); document.documentElement.scrollTop = 0; }, [location.pathname]);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) { return <AuthCallback />; }
  const hideChrome = [
    '/admin', '/admin/clientes', '/panel', '/partners/panel', '/agencias/panel',
    '/clinica/panel', '/clinica/app', '/clinic-demo-dashboard', '/mlm/panel',
    '/admin/redes', '/tiktok/oauth/callback', '/meta/oauth/callback', '/activa-peru',
    '/acceso-academy', '/academy/mi-perfil', '/academy/admin', '/academy/pago-ok',
    '/unirse', '/partners/quiz',
  ].includes(location.pathname) || location.pathname.startsWith('/academy/aula/');

  return (
    <>
      {!hideChrome && <Navigation />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/soluciones" element={<SolucionesPage />} />
        <Route path="/producto" element={<ProductPage />} />
        <Route path="/que-es" element={<WhatIsPage />} />
        <Route path="/comparativa" element={<ComparisonPage />} />
        <Route path="/developer" element={<DeveloperPage />} />
        <Route path="/seguridad" element={<SecurityPage />} />
        <Route path="/nosotros" element={<NosotrosPage />} />
        <Route path="/contacto" element={<ContactoPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/agencias" element={<AgencyPage />} />
        <Route path="/agenda" element={<AgendaPage />} />
        <Route path="/activar" element={<ActivationFlowPage />} />
        <Route path="/precios" element={<PreciosPage />} />
        <Route path="/servicios" element={<PreciosPage />} />
        <Route path="/sostenibilidad" element={<SostenibilidadPage />} />
        <Route path="/blog" element={<NeuraliaTesixPage />} />
        <Route path="/blog/:slug" element={<NeuraliaTesixPage />} />
        <Route path="/media-demos" element={<MediaDemosPage />} />
        <Route path="/clinical-demo-form" element={<ClinicalDemoFormPage />} />
        <Route path="/clinic-demo-dashboard" element={<ClinicDemoDashboardPage />} />
        <Route path="/sistema-multiagente-lima" element={<GeoLimaPage />} />
        <Route path="/sistema-multiagente" element={<SistemaMultiagenteCorePage />} />
        <Route path="/seguridad-agentes" element={<SeguridadAgentesPage />} />
        <Route path="/crm-clinico-ia" element={<CRMLandingPage />} />
        <Route path="/neuralia369tesix-peru" element={<TesixPeruLandingPage />} />
        <Route path="/consultoria-ia-espana" element={<ConsultoriaEspanaPage />} />
        <Route path="/automatizacion-ia-puerto-rico" element={<AutomatizacionPuertoRicoPage />} />
        <Route path="/sistema-multiagente-brasil" element={<SistemaBrasilPage />} />
        <Route path="/agentes-ia-empresas-peru" element={<AgentesPeruPage />} />
        <Route path="/activa-peru" element={<ActivaPeruPage />} />
        <Route path="/autorizar/:token" element={<AuthLinkAuthorizationPage />} />
        <Route path="/neuralia369tesix" element={<NeuraliaTesixPage />} />
        <Route path="/neuraliatesix" element={<NeuraliaTesixPage />} />
        <Route path="/pago-exitoso" element={<PagoExitosoPage />} />
        <Route path="/pago-cancelado" element={<PagoCanceladoPage />} />
        <Route path="/firma-completada" element={<ContractCompletionPage />} />
        <Route path="/conectar-google" element={<GoogleAccessPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/acceso" element={<LoginPage />} />
        <Route path="/acceso-admin" element={<LoginPage />} />
        <Route path="/acceso-partners" element={<LoginPage />} />
        <Route path="/acceso-agencias" element={<LoginPage />} />
        <Route path="/acceso-clinicas" element={<LoginPage />} />
        <Route path="/acceso-pacientes" element={<LoginPage />} />
        <Route path="/acceso-mlm" element={<LoginPage />} />
        <Route path="/mlm/panel" element={<MlmWorkspacePage />} />
        <Route path="/admin/redes" element={<AdminRedesPage />} />
        <Route path="/tiktok/oauth/callback" element={<TikTokCallbackPage />} />
        <Route path="/meta/oauth/callback" element={<MetaCallbackPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/recursos" element={<RecursosPage />} />
        <Route path="/recursos/sistema-multiagente-2027" element={<ArticuloMultiagente />} />
        <Route path="/recursos/deepseek-vs-gpt-vs-claude" element={<ArticuloDeepSeek />} />
        <Route path="/recursos/seguridad-agentes-ia-proveedor" element={<ArticuloSeguridad />} />
        <Route path="/integraciones/deepseek" element={<DeepSeekPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/app-movil" element={<AppMovilPage />} />
        <Route path="/tendencias" element={<TendenciasPage />} />
        <Route path="/admin/clientes" element={<AdminClientProvisioningPage />} />
        <Route path="/catalogo-agentes" element={<AgentCatalogPage />} />
        <Route path="/clinica/panel" element={<ClinicWorkspacePage />} />
        <Route path="/clinica/app" element={<ClinicPatientAppPage />} />
        <Route path="/experiencia" element={<Experiencia3DPage />} />
        <Route path="/contrato/:contractId" element={<ContractSignPage />} />
        <Route path="/portal" element={<PortalClientePage />} />
        <Route path="/experiencia-3d" element={<Experiencia3DPage />} />
        <Route path="/campana" element={<CampanaPage />} />
        <Route path="/lanzamiento" element={<CampanaPage />} />
        <Route path="/unirse" element={<UnirsePage />} />
        <Route path="/partners/quiz" element={<UnirsePage />} />
        <Route path="/panel" element={<UserDashboard />} />
        <Route path="/partners/panel" element={<UserDashboard />} />
        <Route path="/agencias/panel" element={<UserDashboard />} />
        <Route path="/aviso-legal" element={<AvisoLegal />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/academy" element={<AcademyCatalogPage />} />
        <Route path="/academy/admin" element={<AcademyAdminPage />} />
        <Route path="/academy/pago-ok" element={<AcademyPaymentSuccess />} />
        <Route path="/academy/mi-perfil" element={<AcademyDashboardPage />} />
        <Route path="/academy/verificar/:code" element={<AcademyCertVerifyPage />} />
        <Route path="/academy/aula/:courseId" element={<AcademyClassroomPage />} />
        <Route path="/academy/:courseId" element={<AcademyCoursePage />} />
        <Route path="/acceso-academy" element={<AcademyLoginPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {!hideChrome && <Footer />}
      {!hideChrome && <WhatsAppButton />}
      {!hideChrome && <AynaraVoiceOrb />}
      {!hideChrome && <ChatBot />}
      {!hideChrome && <CookieBanner />}
      <Toaster position="top-right" richColors closeButton />
    </>
  );
}

function App() {
  useEffect(() => {
    const originalConsoleError = console.error;
    const suppressResizeObserverError = (event) => {
      const message = event?.message || event?.reason?.message || '';
      if (message.includes('ResizeObserver loop completed with undelivered notifications') || message.includes('ResizeObserver loop limit exceeded')) {
        event.stopImmediatePropagation?.();
        event.preventDefault?.();
      }
    };

    console.error = (...args) => {
      const joined = args.map((item) => (typeof item === 'string' ? item : item?.message || '')).join(' ');
      if (joined.includes('ResizeObserver loop completed with undelivered notifications') || joined.includes('ResizeObserver loop limit exceeded')) {
        return;
      }
      originalConsoleError(...args);
    };

    window.addEventListener('error', suppressResizeObserverError, true);
    window.addEventListener('unhandledrejection', suppressResizeObserverError, true);

    return () => {
      console.error = originalConsoleError;
      window.removeEventListener('error', suppressResizeObserverError, true);
      window.removeEventListener('unhandledrejection', suppressResizeObserverError, true);
    };
  }, []);

  useEffect(() => {
    const consent = document.cookie.split('; ').find((row) => row.startsWith('neuralia369_cookies='));
    if (consent?.includes('accepted')) {
      syncTikTokConsent('accepted');
    }
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App bg-background min-h-screen">
          <BrowserRouter>
            <AnalyticsManager />
            <AppShell />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
